import { ApolloError, isApolloError } from 'apollo-boost'
import { GraphQLError } from 'graphql'
import { ILogger } from '@qest/logger-utils'
import { GQLExceptionCodes } from '~/graphql/__generated__/interfaces'

export interface ApiValidationError {
  field: string
  message: string
  keyword: string
}

export type ApiValidationErrors = Record<string, ApiValidationError[]>

export const parseGqlValidationErrors = (
  apolloError: ApolloError
): ApiValidationErrors => {
  const apiValidationErrors: ApiValidationErrors = {}
  if (apolloError.graphQLErrors) {
    apolloError.graphQLErrors.forEach((gqlError: GraphQLError) => {
      const extensions = gqlError.extensions
      if (
        extensions &&
        extensions.code === GQLExceptionCodes.ValidationError &&
        Array.isArray(extensions.errors)
      ) {
        extensions.errors.forEach((validationError: ApiValidationError) => {
          const fieldValidationErrors: ApiValidationError[] =
            apiValidationErrors[validationError.field] ?? []
          fieldValidationErrors.push(validationError)
          apiValidationErrors[validationError.field] = fieldValidationErrors
        })
      }
    })
  }
  return apiValidationErrors
}

export const parseGqlErrorCodes = (
  apolloError: ApolloError
): GQLExceptionCodes[] => {
  const codes: GQLExceptionCodes[] = []
  if (apolloError.graphQLErrors) {
    apolloError.graphQLErrors.forEach((gqlError: GraphQLError) => {
      const extensions = gqlError.extensions
      if (extensions && extensions.code) {
        codes.push(extensions.code)
      }
    })
  }
  return codes
}

export const isValidationError = (e: any): boolean => {
  if (!isApolloError(e)) {
    return false
  }
  return parseGqlErrorCodes(e).includes(GQLExceptionCodes.ValidationError)
}

export const isForbiddenError = (e: any): boolean => {
  if (!isApolloError(e)) {
    return false
  }
  return parseGqlErrorCodes(e).includes(GQLExceptionCodes.Forbidden)
}

export const isGqlErrorCodeCritical = (code: GQLExceptionCodes): boolean => {
  return [
    GQLExceptionCodes.InternalServerError,
    GQLExceptionCodes.BadRequest,
    GQLExceptionCodes.Conflict,
  ].includes(code)
}

export const isCriticalError = (e: any): boolean => {
  if (!isApolloError(e)) {
    return true
  }
  const codes = parseGqlErrorCodes(e)
  return codes.some((code: GQLExceptionCodes) => {
    return isGqlErrorCodeCritical(code)
  })
}

const containsHTTP = (msg: string): boolean => {
  return /(http(s?)):\/\//i.test(msg)
}

export const logApolloError = (e: Error, scope: string, logger: ILogger) => {
  let msg = `${e}`

  if (isApolloError(e) && containsHTTP(msg)) {
    if (/(request failed)/i.test(msg)) {
      msg = 'Error: GraphQL error: Request failed'
    }
    if (/(ECONNREFUSED)/i.test(msg)) {
      msg = 'Error: GraphQL error: ECONNREFUSED'
    }
  }

  const message = `${scope} : ${msg}`
  if (isCriticalError(e)) {
    logger.error(message, e)
  } else {
    logger.info(message, e)
  }
}
